import type { MarketingMonth } from '../../../hooks/pipelineTracker/useMarketingReport';
import { CHART_PALETTE } from './trackerKit';

/**
 * The Marketing tab's headline figures and chart series, derived from the marketing report only.
 *
 * NOTHING NEW IS MEASURED HERE. Every figure is a sum or a ratio of figures the report already
 * returns per month, so each card can be checked against the month table below it. Money stays in
 * integer cents; the components convert once, at presentation.
 */

/**
 * One colour per channel, fixed for the whole tab, so a channel is the same colour in the monthly
 * columns, the mix bar and the table's column dots. Assigned by name order, not by spend: a rank
 * changes as months are entered, and a channel that turned from blue to amber after a save would
 * read as a different channel. The house CHART_PALETTE less its cyan: teal-cyan is the cost per lead
 * colour, and a channel in it would read as a cost.
 */
export const CHANNEL_PALETTE: ReadonlyArray<string> = CHART_PALETTE.filter((c) => c !== '#06b6d4');

export const COST_PER_LEAD_COLOUR = '#14b8a6';

export function channelColours(channels: Iterable<string>): Map<string, string> {
  const unique = [...new Set(channels)].sort((a, b) => a.localeCompare(b, 'en-AU'));
  return new Map(unique.map((name, i) => [name, CHANNEL_PALETTE[i % CHANNEL_PALETTE.length]]));
}

/** A channel's ex GST spend in one month, 0 when the month has no entry for it. */
export const channelCents = (month: MarketingMonth, channel: string): number =>
  month.channels.find((c) => c.channel === channel)?.exGstCents ?? 0;

export interface MarketingSummary {
  /** Ex GST, every month with spend entered. */
  spendCents: number;
  monthsWithSpend: number;
  /** Leads created in the months with spend; null when no month has a Sales export to count from. */
  leads: number | null;
  /** Spend over leads across the months the report gives a cost per lead for (complete months). */
  averageCostPerLeadCents: number | null;
  costPerLeadMonths: number;
  lowest: { month: string; cents: number } | null;
  /** Only when two or more months have a cost per lead: with one, highest and lowest are the same month. */
  highest: { month: string; cents: number } | null;
}

export function marketingSummary(months: readonly MarketingMonth[]): MarketingSummary {
  const spendCents = months.reduce((sum, m) => sum + m.spendExGstCents, 0);
  const counted = months.filter((m) => m.leadsCreated !== null);
  const leads = counted.length === 0 ? null : counted.reduce((sum, m) => sum + (m.leadsCreated ?? 0), 0);
  // The months the server priced: complete months with leads. The average is pooled (total spend
  // over total leads), not a mean of monthly ratios, so a quiet month does not weigh as much as a
  // busy one.
  const priced = months.filter((m) => m.costPerLeadCents !== null && (m.leadsCreated ?? 0) > 0);
  const pricedSpend = priced.reduce((sum, m) => sum + m.spendExGstCents, 0);
  const pricedLeads = priced.reduce((sum, m) => sum + (m.leadsCreated ?? 0), 0);
  const ranked = priced
    .map((m) => ({ month: m.month, cents: m.costPerLeadCents as number }))
    .sort((a, b) => a.cents - b.cents || a.month.localeCompare(b.month));
  return {
    spendCents,
    monthsWithSpend: months.length,
    leads,
    averageCostPerLeadCents: pricedLeads > 0 ? Math.round(pricedSpend / pricedLeads) : null,
    costPerLeadMonths: priced.length,
    lowest: ranked[0] ?? null,
    highest: ranked.length >= 2 ? ranked[ranked.length - 1] : null,
  };
}

export interface MixSlice {
  channel: string;
  cents: number;
  /** Share of the window's spend, 0 to 1. */
  share: number;
}

/** Each channel's share of every month's spend together, largest first; channels with no spend left out. */
export function spendMix(months: readonly MarketingMonth[], channels: readonly string[]): MixSlice[] {
  const totals = channels.map((channel) => ({ channel, cents: months.reduce((sum, m) => sum + channelCents(m, channel), 0) }));
  const all = totals.reduce((sum, t) => sum + t.cents, 0);
  return totals
    .filter((t) => t.cents > 0)
    .map((t) => ({ ...t, share: all > 0 ? t.cents / all : 0 }))
    .sort((a, b) => b.cents - a.cents || a.channel.localeCompare(b.channel, 'en-AU'));
}

/** Whole percent of a share, for labels. */
export const percent = (share: number): string => `${Math.round(share * 100)}%`;

// ---------------------------------------------------------------- reporting period

/**
 * The Marketing period. Spend is entered per month, so every period selects whole months: the
 * months it overlaps, counted from the snapshot date. WTD and MTD both select the snapshot month
 * (a week of a monthly figure is not a figure). ALL is the tab's earlier behaviour, every month with
 * spend entered, and stays the default.
 */
export type MarketingPeriodKey = 'all' | 'wtd' | 'mtd' | 'qtd' | 'fytd' | 'cytd' | 'custom';

/** Inclusive month range, 'YYYY-MM'. */
export interface MonthRange {
  from: string;
  to: string;
}

const ym = (y: number, m: number): string => `${y}-${String(m).padStart(2, '0')}`;

/** The months a period covers, or null for ALL (no filter). `anchor` is the snapshot date, ISO. */
export function periodMonths(
  key: MarketingPeriodKey,
  anchor: string,
  custom?: { from: string; to: string },
): MonthRange | null {
  if (key === 'all') return null;
  if (key === 'custom') {
    if (!custom || !custom.from || !custom.to) return null;
    return { from: custom.from.slice(0, 7), to: custom.to.slice(0, 7) };
  }
  const [y, m] = anchor.split('-').map(Number);
  const to = ym(y, m);
  if (key === 'wtd' || key === 'mtd') return { from: to, to };
  if (key === 'qtd') return { from: ym(y, m - ((m - 1) % 3)), to };
  // Australian financial year, from 1 July.
  if (key === 'fytd') return { from: m >= 7 ? ym(y, 7) : ym(y - 1, 7), to };
  return { from: ym(y, 1), to };
}

/** The months of `months` inside `range`; all of them when `range` is null. */
export function monthsInRange<T extends { month: string }>(months: readonly T[], range: MonthRange | null): T[] {
  return range ? months.filter((m) => m.month >= range.from && m.month <= range.to) : [...months];
}

const SHORT_MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const short = (key: string): { mon: string; year: string } => {
  const [y, m] = key.split('-').map(Number);
  return { mon: SHORT_MONTH[m - 1] ?? key, year: String(y) };
};

/** 'Sep 2026', 'Jul to Sep 2026', 'Nov 2025 to Feb 2026'. */
export function rangeLabel(range: MonthRange): string {
  const a = short(range.from);
  const b = short(range.to);
  if (range.from === range.to) return `${b.mon} ${b.year}`;
  return a.year === b.year ? `${a.mon} to ${b.mon} ${b.year}` : `${a.mon} ${a.year} to ${b.mon} ${b.year}`;
}

/** The chip beside the period bar, saying which months the choice selects. */
export function periodChip(key: MarketingPeriodKey, range: MonthRange | null): string {
  if (range === null) return 'All months with spend';
  const months = rangeLabel(range);
  if (key === 'wtd' || key === 'mtd') return `${key.toUpperCase()} · ${months}, the snapshot month (spend is monthly)`;
  return `${key === 'custom' ? 'Custom' : key.toUpperCase()} · ${months}`;
}
